import { useEffect, useMemo, useRef, useState } from "react";

import { Icon } from "../Icon.jsx";

const EPSILON = 0.000001;
const quantityFormatter = new Intl.NumberFormat("id-ID", {
  maximumFractionDigits: 6,
});

function finiteNumber(value, fallback = null) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function roundQuantity(value) {
  return Math.round((finiteNumber(value, 0) + Number.EPSILON) * 1_000_000) / 1_000_000;
}

function cleanString(value) {
  const text = String(value ?? "").trim();
  return text || null;
}

function stockKey(item) {
  return `${Number(item?.warehouseId)}::${String(item?.skuCode || item?.itemNo || "")
    .trim()
    .toUpperCase()}`;
}

function normalizeItem(item) {
  const sourceQuantity = finiteNumber(item?.sourceQuantity ?? item?.quantity, 0);
  const remainingSmallestQuantity = finiteNumber(
    item?.remainingSmallestQuantity ?? item?.baseQuantityRequired,
    0,
  );
  const calculatedFactor = sourceQuantity > EPSILON
    ? remainingSmallestQuantity / sourceQuantity
    : null;
  const factorToSmallestUom = finiteNumber(
    item?.factorToSmallestUom,
    calculatedFactor,
  );
  const sourceUnitName = cleanString(item?.sourceUnitName ?? item?.itemUnitName);
  const smallestUnitName = cleanString(item?.smallestUnitName) ||
    (Math.abs((factorToSmallestUom || 0) - 1) <= EPSILON
      ? sourceUnitName
      : null);
  const available = Math.max(
    0,
    finiteNumber(item?.availableBefore ?? item?.baseStockAvailable, 0),
  );

  return {
    ...item,
    lineIndex: Number(item?.lineIndex),
    itemNo: cleanString(item?.itemNo ?? item?.skuCode),
    itemName: cleanString(item?.itemName) || cleanString(item?.itemNo) || "Item",
    sourceQuantity,
    originalSourceQuantity: finiteNumber(item?.originalSourceQuantity, sourceQuantity),
    deliveredSourceQuantity: Math.max(
      0,
      finiteNumber(item?.deliveredSourceQuantity, 0),
    ),
    sourceUnitName,
    smallestUnitName,
    factorToSmallestUom,
    remainingSmallestQuantity,
    availableSeed: available,
    warehouseId: Number(item?.warehouseId),
    skuCode: cleanString(item?.skuCode ?? item?.itemNo),
  };
}

function selectionMap(selections) {
  return new Map(
    (Array.isArray(selections) ? selections : []).map((entry) => [
      Number(entry.lineIndex),
      entry,
    ]),
  );
}

export function buildClientItemEditor(items, manualSelections = []) {
  const normalizedItems = (items || []).map(normalizeItem);
  const manualByLine = selectionMap(manualSelections);
  const initialLedger = new Map();

  normalizedItems.forEach((item) => {
    const key = stockKey(item);
    if (!initialLedger.has(key)) initialLedger.set(key, item.availableSeed);
  });

  const ledger = new Map(initialLedger);
  const rows = normalizedItems.map((item) => {
    const key = stockKey(item);
    const availableBefore = Math.max(0, finiteNumber(ledger.get(key), 0));
    const maxQuantity = roundQuantity(
      Math.min(item.remainingSmallestQuantity, availableBefore),
    );
    const manual = manualByLine.get(item.lineIndex);
    const validData = Boolean(
      Number.isInteger(item.lineIndex) &&
        item.lineIndex >= 0 &&
        item.itemNo &&
        item.smallestUnitName &&
        item.factorToSmallestUom > EPSILON &&
        item.remainingSmallestQuantity >= 0,
    );

    let quantitySmallestUom = null;
    let editorState = "MANUAL_REQUIRED";
    let editorMessage =
      "Stok tidak mencukupi untuk seluruh sisa SO. Atur Qty DO terlebih dahulu.";
    let editedManually = Boolean(manual);

    if (!validData) {
      editorState = "DATA_NOT_READY";
      editorMessage = "Satuan terkecil atau konversi item belum lengkap.";
      editedManually = false;
    } else if (manual) {
      const selected = finiteNumber(manual.quantitySmallestUom);
      quantitySmallestUom = selected;
      if (selected === null || selected < -EPSILON) {
        editorState = "REVIEW_REQUIRED";
        editorMessage = "Qty DO harus berupa angka nol atau lebih besar.";
      } else if (selected - item.remainingSmallestQuantity > EPSILON) {
        editorState = "REVIEW_REQUIRED";
        editorMessage = "Qty DO melebihi sisa Sales Order.";
      } else if (selected - availableBefore > EPSILON) {
        editorState = "REVIEW_REQUIRED";
        editorMessage = "Qty DO melebihi stok yang tersedia untuk urutan ini.";
      } else {
        quantitySmallestUom = roundQuantity(Math.max(0, selected));
        editorState = quantitySmallestUom <= EPSILON
          ? "EXCLUDED_MANUAL"
          : "USER_EDITED";
        editorMessage = quantitySmallestUom <= EPSILON
          ? "Item tidak dimasukkan ke Delivery Order ini."
          : "Qty DO sudah diatur manual.";
      }
    } else if (availableBefore <= EPSILON) {
      quantitySmallestUom = 0;
      editorState = "STOCK_EMPTY";
      editorMessage = "Stok kosong — item tidak dimasukkan ke DO ini.";
    } else if (availableBefore + EPSILON >= item.remainingSmallestQuantity) {
      quantitySmallestUom = roundQuantity(item.remainingSmallestQuantity);
      editorState = "READY";
      editorMessage = "Seluruh sisa SO dapat dimasukkan ke DO.";
    }

    const validSelection = [
      "READY",
      "USER_EDITED",
      "EXCLUDED_MANUAL",
      "STOCK_EMPTY",
    ].includes(editorState);
    const consumed = validSelection
      ? Math.max(0, finiteNumber(quantitySmallestUom, 0))
      : 0;
    const availableAfter = roundQuantity(Math.max(0, availableBefore - consumed));
    if (validSelection) ledger.set(key, availableAfter);

    return {
      ...item,
      key,
      availableBefore,
      availableAfter,
      maxQuantity,
      quantitySmallestUom,
      sourceEquivalentQuantity:
        quantitySmallestUom === null
          ? null
          : roundQuantity(quantitySmallestUom / item.factorToSmallestUom),
      editorState,
      editorMessage,
      editedManually,
      partialLine:
        quantitySmallestUom !== null &&
        quantitySmallestUom + EPSILON < item.remainingSmallestQuantity,
    };
  });

  const unresolvedCount = rows.filter((row) =>
    ["MANUAL_REQUIRED", "REVIEW_REQUIRED", "DATA_NOT_READY"].includes(
      row.editorState,
    ),
  ).length;
  const positiveCount = rows.filter(
    (row) => finiteNumber(row.quantitySmallestUom, 0) > EPSILON,
  ).length;
  const ready = rows.length > 0 && unresolvedCount === 0 && positiveCount > 0;
  const deliveryMode = rows.some((row) => row.partialLine) ? "PARTIAL" : "FULL";

  return {
    rows,
    ready,
    deliveryMode,
    itemSelections: rows
      .filter((row) => row.quantitySmallestUom !== null)
      .map((row) => ({
        lineIndex: row.lineIndex,
        quantitySmallestUom: row.quantitySmallestUom,
      })),
    manualSelections: rows
      .filter((row) => row.editedManually && row.quantitySmallestUom !== null)
      .map((row) => ({
        lineIndex: row.lineIndex,
        quantitySmallestUom: row.quantitySmallestUom,
      })),
    summary: {
      total: rows.length,
      ready: rows.filter((row) => row.editorState === "READY").length,
      manual: rows.filter((row) => row.editorState === "USER_EDITED").length,
      manualRequired: rows.filter((row) => row.editorState === "MANUAL_REQUIRED").length,
      reviewRequired: rows.filter((row) => row.editorState === "REVIEW_REQUIRED").length,
      stockEmpty: rows.filter((row) => row.editorState === "STOCK_EMPTY").length,
      excluded: rows.filter((row) =>
        ["EXCLUDED_MANUAL", "STOCK_EMPTY"].includes(row.editorState),
      ).length,
      dataIssues: rows.filter((row) => row.editorState === "DATA_NOT_READY").length,
      positive: positiveCount,
    },
  };
}

function formatQuantity(value) {
  if (value === null || value === undefined || value === "") return "—";
  return quantityFormatter.format(Number(value) || 0);
}

function stateLabel(state) {
  return {
    READY: "Siap",
    USER_EDITED: "Diatur manual",
    EXCLUDED_MANUAL: "Tidak dimasukkan",
    MANUAL_REQUIRED: "Perlu diatur",
    REVIEW_REQUIRED: "Perlu ditinjau ulang",
    STOCK_EMPTY: "Stok kosong",
    DATA_NOT_READY: "Data belum siap",
  }[state] || "Perlu diperiksa";
}

export function DoItemQuantityEditor({
  disabled = false,
  initialSelections = [],
  items = [],
  onChange,
  onWarehouseChange,
  resetKey = "",
  warehouseBusy = false,
  warehouses = [],
}) {
  const [manualSelections, setManualSelections] = useState(initialSelections);
  const [editingLine, setEditingLine] = useState(null);
  const [draftQuantity, setDraftQuantity] = useState("");
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setManualSelections(initialSelections || []);
    setEditingLine(null);
    setDraftQuantity("");
  }, [resetKey]);

  const editor = useMemo(
    () => buildClientItemEditor(items, manualSelections),
    [items, manualSelections],
  );

  useEffect(() => {
    onChangeRef.current?.(editor);
  }, [editor]);

  function beginEdit(row) {
    const suggested = row.quantitySmallestUom === null
      ? row.maxQuantity
      : row.quantitySmallestUom;
    setEditingLine(row.lineIndex);
    setDraftQuantity(String(suggested ?? 0));
  }

  function cancelEdit() {
    setEditingLine(null);
    setDraftQuantity("");
  }

  function saveEdit(row) {
    const parsed = finiteNumber(String(draftQuantity).replace(",", "."));
    if (parsed === null) return;
    setManualSelections((current) => [
      ...current.filter((entry) => Number(entry.lineIndex) !== row.lineIndex),
      { lineIndex: row.lineIndex, quantitySmallestUom: parsed },
    ]);
    cancelEdit();
  }

  function restoreDefault(row) {
    setManualSelections((current) =>
      current.filter((entry) => Number(entry.lineIndex) !== row.lineIndex),
    );
    cancelEdit();
  }

  return (
    <section className="do-item-editor" aria-label="Atur kuantitas Delivery Order">
      <header className="do-item-editor__header">
        <div>
          <strong>Item dan Qty Delivery Order</strong>
          <span>
            Qty menggunakan satuan terkecil. Sistem menentukan DO penuh atau sebagian dari hasil akhir.
          </span>
        </div>
        <div className="do-item-editor__summary" aria-live="polite">
          {editor.ready && (
            <span className={editor.deliveryMode === "PARTIAL" ? "is-result-partial" : "is-result-full"}>
              Hasil: DO {editor.deliveryMode === "PARTIAL" ? "sebagian" : "penuh"}
            </span>
          )}
          <span><b>{editor.summary.ready + editor.summary.manual}</b> siap</span>
          {editor.summary.manualRequired > 0 && (
            <span className="is-attention"><b>{editor.summary.manualRequired}</b> perlu diatur</span>
          )}
          {editor.summary.stockEmpty > 0 && (
            <span><b>{editor.summary.stockEmpty}</b> stok kosong</span>
          )}
        </div>
      </header>

      <div className="do-item-editor__table-wrap">
        <table className="do-item-editor__table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Qty SO</th>
              <th>Sudah dikirim</th>
              <th>Sisa SO</th>
              <th>Gudang</th>
              <th>Stok tersedia</th>
              <th>Qty DO</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {editor.rows.map((row) => {
              const editing = editingLine === row.lineIndex;
              const showEquivalent = Boolean(
                row.smallestUnitName &&
                  row.sourceUnitName &&
                  (Math.abs(row.factorToSmallestUom - 1) > EPSILON ||
                    row.smallestUnitName.trim().toLowerCase() !==
                      row.sourceUnitName.trim().toLowerCase()),
              );
              const attention = [
                "MANUAL_REQUIRED",
                "REVIEW_REQUIRED",
                "DATA_NOT_READY",
              ].includes(row.editorState);
              return (
                <tr
                  className={attention ? "is-attention" : row.editedManually ? "is-manual" : ""}
                  key={`${row.lineIndex}:${row.itemNo}`}
                >
                  <td data-label="Item">
                    <strong>{row.itemName}</strong>
                    <small>{row.itemNo || "Kode belum tersedia"}</small>
                  </td>
                  <td data-label="Qty SO">
                    {formatQuantity(row.originalSourceQuantity)} {row.sourceUnitName || ""}
                    {showEquivalent && (
                      <small>
                        Setara {formatQuantity(row.originalSourceQuantity * row.factorToSmallestUom)} {row.smallestUnitName || ""}
                      </small>
                    )}
                  </td>
                  <td data-label="Sudah dikirim">
                    {formatQuantity(row.deliveredSourceQuantity)} {row.sourceUnitName || ""}
                  </td>
                  <td data-label="Sisa SO">
                    <strong>{formatQuantity(row.remainingSmallestQuantity)} {row.smallestUnitName || ""}</strong>
                  </td>
                  <td data-label="Gudang" className="do-item-editor__warehouse-cell">
                    {row.preparationItemId && onWarehouseChange ? (
                      <select
                        aria-label={`Gudang ${row.itemName}`}
                        className="do-item-editor__warehouse-select"
                        disabled={disabled || warehouseBusy}
                        onChange={(event) =>
                          onWarehouseChange(row, Number(event.target.value))
                        }
                        value={Number.isInteger(Number(row.warehouseId)) ? String(row.warehouseId) : ""}
                      >
                        <option value="">Pilih gudang</option>
                        {warehouses
                          .filter((warehouse) => warehouse.active !== false)
                          .map((warehouse) => (
                            <option
                              key={warehouse.warehouseId}
                              value={warehouse.warehouseId}
                            >
                              {warehouse.warehouseName}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <strong>{row.warehouseName || "Belum ditentukan"}</strong>
                    )}
                    <small>
                      {warehouseBusy
                        ? "Memperbarui stok…"
                        : "Stok dihitung dari gudang ini"}
                    </small>
                  </td>
                  <td data-label="Stok tersedia">
                    {formatQuantity(row.availableBefore)} {row.smallestUnitName || ""}
                  </td>
                  <td data-label="Qty DO" className="do-item-editor__quantity-cell">
                    {editing ? (
                      <div className="do-item-editor__inline-edit">
                        <label>
                          <input
                            aria-label={`Qty DO ${row.itemName}`}
                            autoFocus
                            disabled={disabled}
                            inputMode="decimal"
                            max={row.maxQuantity}
                            min="0"
                            onChange={(event) => setDraftQuantity(event.target.value)}
                            step="any"
                            type="number"
                            value={draftQuantity}
                          />
                          <em>{row.smallestUnitName || "unit"}</em>
                        </label>
                        <div>
                          <button disabled={disabled} onClick={() => saveEdit(row)} type="button">
                            Simpan
                          </button>
                          <button disabled={disabled} onClick={cancelEdit} type="button">
                            Batal
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="do-item-editor__quantity-display">
                        <strong>
                          {row.quantitySmallestUom === null
                            ? "Perlu diatur"
                            : `${formatQuantity(row.quantitySmallestUom)} ${row.smallestUnitName || ""}`}
                        </strong>
                        {row.editorState !== "DATA_NOT_READY" && row.maxQuantity > EPSILON && (
                          <button disabled={disabled} onClick={() => beginEdit(row)} type="button">
                            {["MANUAL_REQUIRED", "REVIEW_REQUIRED"].includes(row.editorState)
                              ? "Atur qty"
                              : "Edit qty"}
                          </button>
                        )}
                        {row.editedManually && (
                          <button className="is-reset" disabled={disabled} onClick={() => restoreDefault(row)} type="button">
                            Gunakan qty awal
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td data-label="Status">
                    <span className={`do-item-editor__status is-${row.editorState.toLowerCase()}`}>
                      {attention && <Icon name="warning" size={14} />}
                      {stateLabel(row.editorState)}
                    </span>
                    {row.editorState !== "READY" && (
                      <small>{row.editorMessage}</small>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <footer className={`do-item-editor__result is-${editor.deliveryMode.toLowerCase()}`}>
        <Icon name={editor.ready ? "check" : "warning"} size={17} />
        <div>
          <strong>
            {editor.ready
              ? editor.deliveryMode === "FULL"
                ? "Delivery Order penuh"
                : "Delivery Order sebagian"
              : "Qty DO belum siap"}
          </strong>
          <span>
            {editor.ready
              ? editor.deliveryMode === "FULL"
                ? "Seluruh sisa item akan dikirim."
                : "Sebagian item masih tersisa setelah DO ini."
              : "Selesaikan item yang perlu diatur sebelum membuat Delivery Order."}
          </span>
        </div>
      </footer>
    </section>
  );
}
